import React from 'react';
import { Navigate } from 'react-router-dom';
import { useFeatureFlags } from '../hooks/useGlobalSettings';
import { useTenantSettings } from '../hooks/useTenantSettings';
import { useUser } from '../contexts/UserContext';
import LoadingScreen from './common/LoadingScreen';

interface FeatureRouteProps {
    feature: string;
    children: React.ReactElement;
    fallback?: string;
}

/**
 * Feature-protected route component
 * Only allows access if the specified feature is enabled
 * Respects both global and tenant-level feature activation
 */
const FeatureRoute: React.FC<FeatureRouteProps> = ({ feature, children, fallback = '/dashboard' }) => {
    const { isFeatureEnabled, loading: globalLoading } = useFeatureFlags();
    const { isFeatureEnabledForTenant, loading: tenantLoading } = useTenantSettings();
    const { isGlobalAdmin } = useUser();

    if (globalLoading || tenantLoading) {
        return <LoadingScreen />;
    }

    // Global admins only need global feature enabled
    if (isGlobalAdmin()) {
        if (!isFeatureEnabled(feature)) {
            return <Navigate to={fallback} replace />;
        }
    } else {
        // All other users (including tenant admins) need tenant-level feature enabled
        if (!isFeatureEnabledForTenant(feature)) {
            return <Navigate to={fallback} replace />;
        }
    }

    return children;
};

export default FeatureRoute;
